"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/core";
import { ArrowLeft, ChevronDown, FileText, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  fetchWikiBacklinks,
  fetchWikiPage,
  updateWikiPage,
  type WikiPageResponse,
} from "@/api/wiki";
import { viewRefKey, type ViewRef } from "@/api/views";
import { MarkdownOutline, MarkdownOutlineTop } from "@/components/markdown-outline";
import { MarkdownViewer } from "@/components/markdown-viewer";
import { MarkdownViewerToolbar } from "@/components/markdown-viewer-toolbar";
import { MarkdownViewerSaveStatus } from "@/components/markdown-viewer-save-status";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Spinner } from "@/components/ui/spinner";
import { useMarkdownFile } from "@/hooks/use-markdown-file";
import type { AccessTokenGetter } from "@/lib/utils";

export interface WikiPageViewProps {
  viewRef: ViewRef;
  wikiPath: string;
  getToken: AccessTokenGetter;
  onBack: () => void;
  onClose: () => void;
  onNavigatePage: (wikiPath: string) => void;
  className?: string;
}

function fileNameFor(path: string): string {
  const name = path.split("/").at(-1) || "page";
  return `${name}.md`;
}

/** Read, edit, and traverse one wiki page while retaining history in the parent. */
export function WikiPageView({
  viewRef,
  wikiPath,
  getToken,
  onBack,
  onClose,
  onNavigatePage,
  className,
}: WikiPageViewProps) {
  const { t } = useTranslation();
  const viewKey = viewRefKey(viewRef);
  const [page, setPage] = useState<WikiPageResponse | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageRetry, setPageRetry] = useState(0);
  const [backlinks, setBacklinks] = useState<string[]>([]);
  const [backlinksLoading, setBacklinksLoading] = useState(false);
  const [backlinksOpen, setBacklinksOpen] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const [editable, setEditable] = useState(false);
  const [savedMarkdown, setSavedMarkdown] = useState<string | null>(null);
  const [currentMarkdown, setCurrentMarkdown] = useState<string | null>(null);
  const [conversationSaving, setConversationSaving] = useState(false);
  const [conversationSaveError, setConversationSaveError] = useState<string | null>(null);
  const currentMarkdownRef = useRef<string | null>(null);
  const dirty =
    savedMarkdown !== null &&
    currentMarkdown !== null &&
    savedMarkdown !== currentMarkdown;

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setPageLoading(true);
    setPageError(null);
    setBacklinks([]);
    setBacklinksOpen(false);
    setEditable(false);
    setSavedMarkdown(null);
    setCurrentMarkdown(null);
    setConversationSaving(false);
    setConversationSaveError(null);
    currentMarkdownRef.current = null;

    void fetchWikiPage(viewRef, wikiPath, getToken)
      .then((response) => {
        if (cancelled) return;
        setPage(response);
        setPageLoading(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPageError(error instanceof Error ? error.message : t("wiki.unableToLoad"));
        setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, pageRetry, t, viewKey, wikiPath]);

  useEffect(() => {
    if (!page) return;
    let cancelled = false;
    setBacklinksLoading(true);
    void fetchWikiBacklinks(viewRef, page.path, getToken)
      .then((response) => {
        if (!cancelled) setBacklinks(response.backlinks);
      })
      .catch(() => {
        if (!cancelled) setBacklinks([]);
      })
      .finally(() => {
        if (!cancelled) setBacklinksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, page, viewKey]);

  const {
    content: signedContent,
    save: saveSignedFile,
    saving: signedFileSaving,
    saveError: signedFileSaveError,
    conflict: markdownConflict,
    remoteChange: markdownRemoteChange,
    saveOutcomeUnknown: markdownSaveOutcomeUnknown,
    checkLatest: checkMarkdownSaveOutcome,
    reloadLatest: reloadMarkdown,
  } = useMarkdownFile(page?.edit_url ?? null, {
    documentIdentity: page?.path ?? wikiPath,
    pollingPaused: false,
    detectOnly: editable || dirty,
  });
  const content = signedContent ?? page?.content ?? null;
  const canEdit = Boolean(
    page?.can_write
    && (page.edit_url || viewRef.scope === "conversation")
  );
  const saving = viewRef.scope === "conversation"
    ? conversationSaving
    : signedFileSaving;
  const saveError = viewRef.scope === "conversation"
    ? conversationSaveError
    : signedFileSaveError;

  const saveConversationPage = useCallback(async (markdown: string): Promise<boolean> => {
    if (viewRef.scope !== "conversation" || !page) return false;
    setConversationSaving(true);
    setConversationSaveError(null);
    try {
      const updated = await updateWikiPage(
        viewRef,
        page.path,
        markdown,
        getToken,
      );
      setPage(updated);
      return true;
    } catch (error) {
      setConversationSaveError(
        error instanceof Error ? error.message : t("wiki.unableToSave"),
      );
      return false;
    } finally {
      setConversationSaving(false);
    }
  }, [getToken, page, t, viewRef]);

  const handleMarkdownLoaded = useCallback((markdown: string) => {
    currentMarkdownRef.current = markdown;
    setSavedMarkdown(markdown);
    setCurrentMarkdown(markdown);
  }, []);

  const handleMarkdownChange = useCallback((markdown: string) => {
    currentMarkdownRef.current = markdown;
    setCurrentMarkdown(markdown);
  }, []);

  const handleSave = useCallback(async (markdown?: string): Promise<boolean> => {
    const markdownToSave = markdown ?? currentMarkdown;
    if (markdownToSave === null) return false;
    if (markdownToSave === savedMarkdown) return true;

    const didSave = viewRef.scope === "conversation"
      ? await saveConversationPage(markdownToSave)
      : await saveSignedFile(markdownToSave);
    if (didSave) {
      setSavedMarkdown(markdownToSave);
      const latestMarkdown = currentMarkdownRef.current ?? markdownToSave;
      currentMarkdownRef.current = latestMarkdown;
      setCurrentMarkdown(latestMarkdown);
    }
    return didSave;
  }, [currentMarkdown, saveConversationPage, saveSignedFile, savedMarkdown, viewRef.scope]);

  const reloadMarkdownAndDiscard = useCallback(async () => {
    if (!window.confirm(t("viewer.reloadDiscardPrompt"))) return;
    const wasEditable = editable;
    setEditable(false);
    const latest = await reloadMarkdown();
    if (latest === null) {
      setEditable(wasEditable);
      return;
    }
    currentMarkdownRef.current = latest;
    setSavedMarkdown(latest);
    setCurrentMarkdown(latest);
  }, [editable, reloadMarkdown, t]);

  const reconcileMarkdownSave = useCallback(async () => {
    const result = await checkMarkdownSaveOutcome();
    if (result.kind !== "reconciled") return;
    setSavedMarkdown(result.content);
    if (currentMarkdownRef.current === null) {
      currentMarkdownRef.current = result.content;
      setCurrentMarkdown(result.content);
    }
  }, [checkMarkdownSaveOutcome]);

  const title = page?.title || fileNameFor(wikiPath).replace(/\.md$/, "");
  const fileName = fileNameFor(page?.path ?? wikiPath);

  return (
    <section
      className={`flex h-full min-w-0 flex-col bg-background ${className ?? ""}`}
      data-testid="wiki-page-view"
    >
      <header className="flex min-h-[53px] shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onBack}
            aria-label={t("wiki.back")}
            title={t("wiki.back")}
            data-testid="wiki-page-back"
          >
            <ArrowLeft className="size-4" />
          </Button>
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <MarkdownViewerSaveStatus
            contentLoaded={currentMarkdown != null}
            editable={editable}
            hasSaveHandler={canEdit && !markdownConflict && !markdownSaveOutcomeUnknown}
            dirty={dirty}
            saving={saving}
            saveError={saveError}
          />
          <span className="truncate text-sm font-semibold" title={title}>
            {title}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <MarkdownViewerToolbar
            editor={editor}
            fileName={fileName}
            getToken={getToken}
            fileUrl={page?.edit_url ?? undefined}
            editable={editable}
            dirty={dirty}
            saving={saving}
            saveError={saveError}
            saveBlocked={Boolean(markdownConflict || markdownSaveOutcomeUnknown)}
            onToggleEdit={canEdit && !markdownConflict && !markdownSaveOutcomeUnknown ? () => setEditable((value) => !value) : undefined}
            onSave={canEdit && !markdownConflict && !markdownSaveOutcomeUnknown ? handleSave : undefined}
            contentVersion={currentMarkdown}
          />
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label={t("wiki.closePage")}
            title={t("wiki.closePage")}
            data-testid="wiki-page-close"
          >
            <X className="size-4" />
          </Button>
        </div>
      </header>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div ref={contentScrollRef} className="h-full min-w-0 overflow-y-auto overflow-x-hidden p-4">
          {pageLoading ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <Spinner className="size-5" />
              <p className="text-sm">{t("wiki.loading")}</p>
            </div>
          ) : pageError ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center">
              <p className="text-sm text-destructive">{pageError}</p>
              <Button variant="outline" size="sm" onClick={() => setPageRetry((value) => value + 1)}>
                {t("common:actions.retry")}
              </Button>
            </div>
          ) : content != null ? (
            <>
              {markdownRemoteChange && !markdownConflict && !markdownSaveOutcomeUnknown && (
                <p className="mb-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  {t("viewer.remoteChange")}
                </p>
              )}
              {markdownSaveOutcomeUnknown && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded border border-amber-500/50 bg-amber-500/5 px-2 py-1.5 text-xs" role="alert">
                  <span>{t("viewer.saveOutcomeUnknown")}</span>
                  <Button size="sm" variant="outline" onClick={() => void reconcileMarkdownSave()}>{t("viewer.checkLatest")}</Button>
                </div>
              )}
              {markdownConflict && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded border border-destructive/50 bg-destructive/5 px-2 py-1.5 text-xs" role="alert">
                  <span>{t("viewer.fileChangedElsewhere")}</span>
                  <Button size="sm" variant="outline" onClick={() => void reloadMarkdownAndDiscard()}>{t("viewer.reloadAndDiscard")}</Button>
                </div>
              )}
              <MarkdownOutlineTop
                editor={editor}
                scrollContainerRef={contentScrollRef}
                contentVersion={currentMarkdown ?? content}
              />
              <MarkdownViewer
                key={page?.path ?? wikiPath}
                content={content}
                fileName={fileName}
                editable={editable}
                saving={saving}
                onEditorReady={setEditor}
                onMarkdownLoaded={handleMarkdownLoaded}
                onMarkdownChange={handleMarkdownChange}
                onNavigateWikiLink={(path) => {
                  if (!editable && !dirty && !saving && !markdownConflict && !markdownSaveOutcomeUnknown) onNavigatePage(path);
                }}
              />
              <Collapsible
                open={backlinksOpen}
                onOpenChange={setBacklinksOpen}
                className="mt-8 border-t pt-3"
                data-testid="wiki-backlinks"
              >
                <CollapsibleTrigger data-testid="wiki-backlinks-trigger">
                  <span>{t("wiki.linkedFrom", { count: backlinks.length })}</span>
                  <ChevronDown className="chevron size-4 transition-transform" />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-3 pb-2">
                  {backlinksLoading ? (
                    <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
                      <Spinner className="size-3.5" />
                      {t("wiki.loading")}
                    </div>
                  ) : backlinks.length ? (
                    <ul className="space-y-1 py-1">
                      {backlinks.map((path) => (
                        <li key={path}>
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto px-0 py-0 text-left"
                            onClick={() => onNavigatePage(path)}
                            data-testid={`wiki-backlink-${path}`}
                          >
                            {path}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="py-2 text-xs text-muted-foreground">{t("wiki.noBacklinks")}</p>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </>
          ) : null}
        </div>
        <MarkdownOutline
          editor={editor}
          scrollContainerRef={contentScrollRef}
          contentVersion={currentMarkdown ?? content}
        />
      </div>
    </section>
  );
}
